/**
 * Composable pour gérer l'auto-remplissage de l'identifiant de structure
 * Pattern: 1 + Licence + 0000
 * Applicable uniquement si la licence est en mode démo (colonne a = 1)
 */

import { computed } from "vue";

export function useStructureIdentifier() {
  /**
   * Génère l'identifiant de structure basé sur le pattern
   * Pattern: 1 + licenceId + 0000
   * @param {string} licenceId - L'identifiant de la licence MS4
   * @returns {string} - L'identifiant généré
   */
  const generateStructureIdentifier = (licenceId) => {
    if (!licenceId) {
      return "";
    }
    return `1${licenceId}0000`;
  };

  /**
   * Détermine si une licence est en mode démo
   * @param {Object} cabinet - L'objet cabinet/licence avec les propriétés
   * @returns {boolean} - true si la licence est en mode démo (a=1)
   */
  const isLicenceDemo = (cabinet) => {
    // Le champ 'demo' est au niveau de la licence individuelle dans licences[]
    // On cherche la licence spécifique sélectionnée (ms4LicenceId) dans la liste
    const licenceId = cabinet?.licenceID || cabinet?.ms4LicenceId;
    if (licenceId && cabinet?.licences?.length) {
      const specificLicence = cabinet.licences.find(
        (l) => l.licenceID === licenceId
      );
      if (specificLicence) return specificLicence.demo === 1;
    }
    // Fallback sur demo au niveau cabinet
    return cabinet && cabinet.demo === 1;
  };

  /**
   * Traite le changement de licence dans la liste déroulante
   * @param {Object} selectedCabinet - Le cabinet/licence sélectionné
   * @param {Object} structure - L'objet structure à mettre à jour
   * @returns {Object} - Les modifications à appliquer
   */
  const handleLicenceChange = (selectedCabinet, structure) => {
    const updates = {
      ...structure,
    };

    // Si la licence est en mode démo, générer l'identifiant
    if (isLicenceDemo(selectedCabinet)) {
      const licenceId =
        selectedCabinet.licenceID ||
        selectedCabinet.ms4LicenceId ||
        selectedCabinet.licence;
      updates.finessNumber = generateStructureIdentifier(licenceId);
      updates.isDemo = true;
      updates.isDemoMode = true; // Flag pour désactiver l'édition
    } else {
      // Mode production: réinitialiser le flag
      updates.isDemo = false;
      updates.isDemoMode = false;
    }

    return updates;
  };

  return {
    generateStructureIdentifier,
    isLicenceDemo,
    handleLicenceChange,
  };
}
